import validator from 'validator'

import {
  Iris,
  Jsonld,
  Iri,
  Literal,
  JsonLiteral,
  DataProperty,
  ObjectProperty,
} from '@/rec-cache/rec-cache-types'

import { RecIndividual } from '@/rec-cache/rec-cache-individual'

// Factory for RDF and OWL types.
class Owl {
  constructor(namespaces) {
    this.namespaces = namespaces
  }

  // Check if an id is an iri within the namespaces. Returns boolean.
  isIri(id) {
    const iri = this.namespaces.expand(id)
    return validator.isURL(iri) == true
  }

  iri(value) {
    const iri = this.namespaces.expand(value)
    return new Iri(iri, this.namespaces)
  }

  literal(value, datatype = Iris.xsdString) {
    return new Literal(value, datatype)
  }

  jsonLiteral(json) {
    return new JsonLiteral(json)
  }

  dataProperty(name, values) {
    return new DataProperty(name, ...values)
  }

  // Parameter name is an Iri and parameter values is an array of Iri.
  objectProperty(name, values) {
    return new ObjectProperty(name, ...values)
  }

  individual() {
    return new RecIndividual(this.namespaces)
  }

  // Parses expanded JSON LD to an OWL individual.
  // Support for application specific RDF namespacing is undertaklen here, ie the Iris are minted (issued) here.
  // TODO combine types, rec individual and owl into one module. Refactor properties to not mint Iris. Must be done beforehand.
  // TODO there seem to be n issue that the expected json-ld individual is not an array, but an object...
  parse(expandedJsonLd) {
    // console.log('OWL PARSE', expandedJsonLd)
    const individual = new RecIndividual()

    Object.entries(expandedJsonLd).forEach(([key, values]) => {
      try {
        switch (key) {
          case Jsonld.context:
            // Context disregarded.
            break
          case Jsonld.id:
            // Id is a string.
            individual.id = this.iri(values)
            break
          case Jsonld.type:
            // Type is an array of strings.
            individual.addTypes(...values.map((v) => this.iri(v)))
            break
          default: {
            // Property values are arrays of objects { @value, [ @language ] }
            // Determine if first element is an IRI implying an object property.
            // TODO add support for language...
            const [value] = values
            const name = this.iri(key)

            // TODO if the value is undefined (an empty array) or if its an empty object the property is created as an empty data property.
            // The property could be skipped but as it exist in the underlying storage system better to show it empty.
            // This is not really an optimum behaviour.
            // If no value is set the algorithm cannot determine if the property is data or object property.
            // May be changed in the future by having the parser undertandign the semantics of what is read.
            if (!value || Object.keys(value).length == 0) {
              const property = this.dataProperty(name, [])
              individual.addProperties(property)
              // console.log('EMPTY VALUE', value, name, individual)
              break
            }

            if (value?.['@type'] == '@json') {
              const property = this.dataProperty(name, [
                this.jsonLiteral(value?.['@value']),
              ])
              // console.log('JSON LITERAL', key, value, property)

              individual.addProperties(property)
              break
            }

            // TODO object properties shall be @id not @value but wrong format from WoB...
            // TODO introduced workaround so that @id based are passed correctly (eg used for ADT)...
            const property =
              value?.[Jsonld.id] ||
              (value?.[Jsonld.value] &&
                validator.isURL(value[Jsonld.value].toString()))
                ? this.objectProperty(
                    name,
                    values.map((v) => this.iri(v[Jsonld.id] || v[Jsonld.value]))
                  )
                : this.dataProperty(
                    name,
                    values.map((v) =>
                      this.literal(
                        v[Jsonld.value].toString(),
                        v[Jsonld.type] || Iris.xsdString
                      )
                    )
                  )
            individual.addProperties(property)
            break
          }
        }
      } catch (error) {
        console.log('rec-cache.owl.parser.error', expandedJsonLd, key, values)
      }
    })

    return individual
  }
}

export { Owl }
